// client/src/pages/sections/groups/BringAlong.js
// "Bring others along?" inside the Change-or-Create-Signer-Group dialog (SplitMemberButton), so a partner who makes a
// child client its own group answers everything in ONE dialog instead of getting a second one after the move.
//
//   useBringAlong(candidates, anchorEntityType)  selection + the mixed-type confirmations the selection needs
//   <BringAlongFields ... />                     the checklist and confirmations
//
// Same rules as MigrateMembersModal.js, which stays as the fallback (inline list unavailable, or the move of the others
// failed and the partner retries): the mixed-type gate is judged on who is JOINING — the new group is just the anchor
// and everyone checked joins it — and the server (move_clients_to_sg) re-validates all of it.

import { useCallback, useEffect, useMemo, useState } from 'react';
import ClientChip from './ClientChip';
import { kindOf, requiredConfirmationsForJoin, MIXED_CONFIRM_LABEL } from './resolveVerbConfig';

export function useBringAlong(candidates, anchorEntityType) {
  const [selected, setSelected] = useState(() => new Set());
  const [confirmChecked, setConfirmChecked] = useState({});   // token -> true

  const requiredConfirm = useMemo(() => {
    const movers = candidates.filter(c => selected.has(c.clientCode)).map(c => kindOf(c.entityType));
    return requiredConfirmationsForJoin([kindOf(anchorEntityType)], movers);
  }, [selected, candidates, anchorEntityType]);

  // Drop a confirmation the selection no longer needs.
  useEffect(() => {
    setConfirmChecked((prev) => {
      const next = {};
      for (const t of requiredConfirm) if (prev[t]) next[t] = true;
      return Object.keys(next).length === Object.keys(prev).length ? prev : next;
    });
  }, [requiredConfirm]);

  const toggle = useCallback((code) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code); else next.add(code);
      return next;
    });
  }, []);
  const selectAll = useCallback(() => setSelected(new Set(candidates.map(c => c.clientCode))), [candidates]);
  const clearAll = useCallback(() => setSelected(new Set()), []);
  const reset = useCallback(() => { setSelected(new Set()); setConfirmChecked({}); }, []);

  return {
    selected, requiredConfirm, confirmChecked, setConfirmChecked,
    ready: requiredConfirm.every(t => confirmChecked[t]),
    toggle, selectAll, clearAll, reset,
  };
}

export function BringAlongFields({ anchorCode, candidates, bring, disabled }) {
  return (
    <div className="cg-resolve-field cg-nlc-section">
      <div className="cg-resolve-field-label">
        Also bring along (optional)
      </div>
      <p className="cg-resolve-confirm-hint">
        These clients are in <ClientChip code={anchorCode} />'s current Signer Group. Check anyone who should move into
        the new group as well. Anyone you leave unchecked stays where they are.
      </p>
      <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
        <button type="button" className="cg-btn cg-btn--secondary"
                disabled={disabled || bring.selected.size === candidates.length} onClick={bring.selectAll}>
          Select all
        </button>
        <button type="button" className="cg-btn cg-btn--secondary"
                disabled={disabled || bring.selected.size === 0} onClick={bring.clearAll}>
          Clear
        </button>
      </div>
      <div style={{ maxHeight: 220, overflowY: 'auto' }}>
        {candidates.map((c) => (
          <label key={c.clientCode} className="cg-resolve-radio-row"
                 style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0' }}>
            <input type="checkbox" checked={bring.selected.has(c.clientCode)} disabled={disabled}
                   onChange={() => bring.toggle(c.clientCode)} />
            <span>
              <ClientChip code={c.clientCode} name={c.clientName} />
              {c.entityType && <span className="cg-cell-hint" style={{ marginLeft: 8 }}>({c.entityType})</span>}
            </span>
          </label>
        ))}
      </div>
      {bring.requiredConfirm.map((token) => (
        <label key={token} className="cg-resolve-radio-row"
               style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 12, padding: '8px 10px', background: '#fff8e6', border: '1px solid #f0d69a', borderRadius: 6 }}>
          <input type="checkbox" checked={Boolean(bring.confirmChecked[token])} disabled={disabled}
                 onChange={(e) => bring.setConfirmChecked((prev) => ({ ...prev, [token]: e.target.checked }))}
                 style={{ marginTop: 2 }} />
          <span>{MIXED_CONFIRM_LABEL[token]}</span>
        </label>
      ))}
    </div>
  );
}
